import {Box, Button, Dialog, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import type {LatLngExpression} from 'leaflet';
import L from 'leaflet';
import {FC, useEffect, useState} from 'react';
import {MapContainer, Marker, TileLayer, useMap} from 'react-leaflet';

import {ScraperItem} from '@shared/models/parser';

import {geocodeCity, type Coordinates} from '@website_frontend/lib/geocoding';

delete (L.Icon.Default.prototype as {_getIconUrl?: unknown})._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface MapPopoverProps {
  item: ScraperItem;
  open: boolean;
  onClose: () => void;
}

interface MapControllerProps {
  center: LatLngExpression;
  zoom: number;
}

function MapController({center, zoom}: MapControllerProps) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);

  return null;
}

export const MapPopover: FC<MapPopoverProps> = props => {
  const {item, open, onClose} = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [apartmentCoords, setApartmentCoords] = useState<Coordinates | null>(null);
  const [loading, setLoading] = useState(true);
  const [largeMapCenter, setLargeMapCenter] = useState<LatLngExpression>([46.6034, 1.8883]); // Center of France
  const [largeMapZoom, setLargeMapZoom] = useState(6);

  // Load geocoding data when popover opens
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function loadGeocoding() {
      setLoading(true);

      // Geocode apartment location (city + postal code)
      const apartment = await geocodeCity(item.city, item.estatePostalCode, item.country);
      if (!cancelled && apartment) {
        setApartmentCoords(apartment.coordinates);
        setLargeMapCenter([
          apartment.coordinates.lat,
          apartment.coordinates.lon,
        ] as LatLngExpression);
        setLargeMapZoom(13); // City level zoom
      }

      if (!cancelled) {
        setLoading(false);
      }
    }

    void loadGeocoding();

    return () => {
      cancelled = true;
    };
  }, [open, item.city, item.estatePostalCode, item.country]);

  const handleCityView = () => {
    if (apartmentCoords) {
      setLargeMapCenter([apartmentCoords.lat, apartmentCoords.lon] as LatLngExpression);
      setLargeMapZoom(13); // City level
    }
  };

  const handleCityViewZoomed = () => {
    if (apartmentCoords) {
      setLargeMapCenter([apartmentCoords.lat, apartmentCoords.lon] as LatLngExpression);
      setLargeMapZoom(16); // Street level
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          width: '100vw',
          maxWidth: 800,
          maxHeight: '90vh',
          overflow: 'auto',
          m: 0,
        },
      }}
    >
      <Stack p={2} gap={2}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Chargement des cartes...
          </Typography>
        ) : (
          <Stack direction={{xs: 'column', md: 'row'}} gap={2} sx={{width: '100%'}}>
            {/* Two small interactive maps */}
            <Stack
              direction={{xs: 'row', md: 'column'}}
              gap={2}
              sx={{
                width: {xs: '100%', md: 'auto'},
                flex: {xs: 'none', md: '0 0 auto'},
              }}
            >
              {/* Map 1: Country view */}
              <Box
                sx={{
                  flex: {xs: '1 1 0', md: 'none'},
                  minWidth: {xs: 0, md: 250},
                  width: {xs: 'auto', md: 250},
                  height: {xs: 200, md: 200},
                }}
              >
                {apartmentCoords ? (
                  <MapContainer
                    center={[apartmentCoords.lat, apartmentCoords.lon] as LatLngExpression}
                    zoom={
                      item.country &&
                      (item.country.toLowerCase() === 'france' ||
                        item.country.toLowerCase() === 'fr')
                        ? 5
                        : 4
                    }
                    zoomControl={false}
                    style={{height: '100%', width: '100%'}}
                  >
                    <TileLayer
                      attribution=""
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[apartmentCoords.lat, apartmentCoords.lon] as LatLngExpression}
                    />
                  </MapContainer>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Données non disponibles
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Map 2: City/Region view */}
              <Box
                sx={{
                  flex: {xs: '1 1 0', md: 'none'},
                  minWidth: {xs: 0, md: 250},
                  width: {xs: 'auto', md: 250},
                  height: {xs: 200, md: 200},
                }}
              >
                {apartmentCoords ? (
                  <MapContainer
                    center={[apartmentCoords.lat, apartmentCoords.lon] as LatLngExpression}
                    zoom={10}
                    zoomControl={false}
                    style={{height: '100%', width: '100%'}}
                  >
                    <TileLayer
                      attribution=""
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[apartmentCoords.lat, apartmentCoords.lon] as LatLngExpression}
                    />
                  </MapContainer>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Données non disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>

            {/* Large interactive map */}
            <Box
              sx={{
                flex: {xs: 'none', md: '1 1 0'},
                width: {xs: '100%', md: 'auto'},
                minWidth: 0,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{mb: 1}}
              >
                <Typography variant="body2" color="text.secondary">
                  {[item.city, item.country].filter(Boolean).join(', ')}
                </Typography>
                <Stack direction="row" gap={1}>
                  <Button
                    size="small"
                    variant={largeMapZoom === 13 ? 'contained' : 'outlined'}
                    onClick={handleCityView}
                    disabled={!apartmentCoords}
                  >
                    Vue ville
                  </Button>
                  <Button
                    size="small"
                    variant={largeMapZoom === 16 ? 'contained' : 'outlined'}
                    onClick={handleCityViewZoomed}
                    disabled={!apartmentCoords}
                  >
                    Vue détaillée
                  </Button>
                </Stack>
              </Stack>
              <Box
                sx={{
                  height: {xs: 400, md: 416}, // 416 = 2 * 200 (small maps) + 1 * 16 (gap between them)
                  width: '100%',
                }}
              >
                {apartmentCoords ? (
                  <MapContainer
                    center={largeMapCenter}
                    zoom={largeMapZoom}
                    style={{height: '100%', width: '100%'}}
                  >
                    <TileLayer
                      attribution=""
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[apartmentCoords.lat, apartmentCoords.lon] as LatLngExpression}
                    />
                    <MapController center={largeMapCenter} zoom={largeMapZoom} />
                  </MapContainer>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Coordonnées non disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>
        )}
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{width: {xs: '100%', md: 'auto'}, alignSelf: {md: 'flex-end'}}}
        >
          Fermer
        </Button>
      </Stack>
    </Dialog>
  );
};

MapPopover.displayName = 'MapPopover';
